// @ts-check
import sortBy from 'lodash/sortBy';
import without from 'lodash/without';
import uniqBy from 'lodash/uniqBy';
import { BADGES_FILTER_OPTIONS, ASSETS_FILTER_OPTIONS } from '../modules/clients/constants';

/**
 * Sort RTC data items
 * @param {array} RTCItems
 * @returns {array} sorted array
 */
export const sortByClientDevelopmentCategory = (RTCItems) => {
  return sortBy(RTCItems, ['name']);
};

/**
 * Get all unique action types for the specific category and the respective count of RTC's
 * @param {array} categoryRTCs
 * @returns {Array <{ value: string, label: string, amount: number }>}
 */
export const getCategoryActionTypes = (categoryRTCs) => {
  return uniqBy(categoryRTCs, 'actionType').reduce((acc, { actionType }) => {
    if (actionType) {
      const categoryRTCAmount = categoryRTCs.filter((categoryRTC) => categoryRTC.actionType === actionType).length;
      return [
        ...acc,
        {
          value: actionType,
          label: actionType,
          amount: categoryRTCAmount,
        },
      ];
    }
    return acc;
  }, []);
};

/**
 * Get filters collection for FilterChips component
 * @param {Object} filters
 * @returns {Array <{ label: string, clear: function }>}
 */
export const getRTCFilterLabels = (filters) => {
  const labels = [];
  if (filters.actionTypes) {
    filters.actionTypes.forEach((actionType) => {
      labels.push({
        label: actionType,
        clear(filters, setFilters) {
          setFilters({
            ...filters,
            actionTypes: without(filters.actionTypes, actionType),
          });
        },
      });
    });
  }

  if (filters.badges) {
    filters.badges.forEach((badge) => {
      labels.push({
        label: BADGES_FILTER_OPTIONS.reduce((acc, curr) => {
          if (curr.value === badge) return `No ${curr.label}`;

          return acc;
        }, ''),
        clear(filters, setFilters) {
          setFilters({
            ...filters,
            badges: without(filters.badges, badge),
          });
        },
      });
    });
  }

  if (filters.assets) {
    filters.assets.forEach((asset) => {
      labels.push({
        label: ASSETS_FILTER_OPTIONS.reduce((acc, curr) => {
          if (curr.value === asset) return curr.label;

          return acc;
        }, ''),
        clear(filters, setFilters) {
          setFilters({
            ...filters,
            assets: without(filters.assets, asset),
          });
        },
      });
    });
  }

  return labels;
};

/**
 * Check if the reason to contact contains next best action criteria for recommended products.
 * @param {Object} RTCItem
 * @returns {boolean} true if RTCItem contains at least one NBA criterion, false if none.
 */
export const hasNBACriteria = ({ nba }) => {
  if (!nba) {
    return false;
  }

  const { divisionId, classId, brandName, groupId, departmentId } = nba;
  return !!(divisionId || classId || brandName || groupId || departmentId);
};

/**
 * Check if the reason to contact contains recommended products criteria.
 * @param {Object} RTCItem
 * @returns {boolean} true if RTCItem contains at least one criterion, false if none.
 */
export const hasRecommendedProductsCriteria = ({ recommendedProducts }) => {
  if (!recommendedProducts) {
    return false;
  }

  return recommendedProducts.some(
    ({ divisionId, classId, brandName, className, hierarchyLevel1 }) =>
      !!(divisionId || classId || brandName || className || hierarchyLevel1),
  );
};

/**
 * Assigns the products to the reason to contact item.
 * For each recommended products criterion there will be corresponding products array.
 * For NBA (next best action) there will be products array as well.
 * @param {Object} rtcItem
 * @param {Array<Object>} rtcProducts
 * @returns {Object} Combined object.
 */
export const getRTCItemWithRecommendedProducts = (rtcItem, rtcProducts) => {
  let currentDataIndex = 0;
  let finalReasonToContact = rtcItem;

  if (hasNBACriteria(rtcItem)) {
    const products = rtcProducts[currentDataIndex] || null;
    const nba = { ...rtcItem.nba, products };

    currentDataIndex++;
    finalReasonToContact = { ...finalReasonToContact, nba };
  }

  if (hasRecommendedProductsCriteria(rtcItem)) {
    const recommendedProducts = rtcItem.recommendedProducts.map((recommendation) => {
      const products = rtcProducts[currentDataIndex] || null;
      currentDataIndex++;

      return {
        ...recommendation,
        products,
      };
    });

    return { ...finalReasonToContact, recommendedProducts };
  }

  return finalReasonToContact;
};
